/**
 * Inventory action - Player checks what they're carrying
 *
 * This is treated as an observable action where the player
 * physically checks their pockets/bag, which NPCs can notice.
 *
 * Unlike scoring which uses capability data, inventory queries
 * the world model directly since items are core entities.
 *
 * Uses four-phase pattern:
 * 1. validate: Always succeeds (no preconditions)
 * 2. execute: Analyze inventory (no world mutations)
 * 3. report: Emit success events and messages
 * 4. blocked: Generate error events (never called since always valid)
 */

import { Action, ActionContext, ValidationResult } from '../../enhanced-types';
import { ISemanticEvent } from '@sharpee/core';
import { TraitType, WearableTrait, IFEntity } from '@sharpee/world-model';
import { IFActions } from '../../constants';
import { ActionMetadata } from '../../../validation';
import { InventoryEventMap } from './inventory-events';
import { InventoryMessages } from './inventory-messages';

/**
 * Shared data passed between execute and report phases
 */
interface InventorySharedData {
  analysis?: InventoryAnalysis;
}

function getInventorySharedData(context: ActionContext): InventorySharedData {
  return context.sharedData as InventorySharedData;
}

interface InventoryAnalysis {
  holding: IFEntity[];
  worn: IFEntity[];
  carried: IFEntity[];
  eventData: InventoryEventMap['if.action.inventory'];
  messageId: string;
  params: Record<string, any>;
  holdingList?: string;
  wornList?: string;
  burdenMessage?: string;
  totalWeight: number;
  weightLimit: number;
}

/**
 * Analyzes the player's inventory and generates data for events
 * Shared logic between validate and execute phases
 */
function analyzeInventory(context: ActionContext): InventoryAnalysis {
  const player = context.player;
  const location = context.currentLocation;
  
  // Query the world model for what the player is carrying
  const carried = context.world.getContents(player.id);
  
  // Separate worn items from held items
  const worn = carried.filter(item => {
    if (item.has(TraitType.WEARABLE)) {
      const wearable = item.get(TraitType.WEARABLE) as WearableTrait;
      return wearable.worn;
    }
    return false;
  });
  
  const holding = carried.filter(item => {
    if (item.has(TraitType.WEARABLE)) {
      const wearable = item.get(TraitType.WEARABLE) as WearableTrait;
      return !wearable.worn;
    }
    return true;
  });
  
  // Calculate weight if player has inventory limits
  let totalWeight = 0;
  let hasWeightLimit = false;
  let weightLimit = 0;
  
  if (player.has(TraitType.ACTOR)) {
    // TODO: Add inventoryLimit to ActorTrait when weight system is implemented
    // Weight-based inventory limits are not yet supported on ActorTrait
  }
  
  // Build event data for the observable action
  const eventData: InventoryEventMap['if.action.inventory'] = {
    actorId: player.id,
    locationId: location.id,
    totalItems: carried.length,
    heldItems: holding.length,
    wornItems: worn.length,
    isEmpty: carried.length === 0,
    carried: holding.map(e => ({ id: e.id, name: e.name })),
    worn: worn.map(e => ({ id: e.id, name: e.name })),
    items: [
      ...holding.map(e => ({ id: e.id, name: e.name, worn: false })),
      ...worn.map(e => ({ id: e.id, name: e.name, worn: true }))
    ]
  };
  
  // Add weight info if applicable
  if (hasWeightLimit) {
    eventData.totalWeight = totalWeight;
    eventData.maxWeight = weightLimit;
    eventData.weightLimit = weightLimit;
    eventData.weightPercentage = Math.round((totalWeight / weightLimit) * 100);
    
    // Determine burden status
    if (carried.length > 0) {
      const percentage = (totalWeight / weightLimit) * 100;
      if (percentage >= 90) {
        eventData.burden = 'overloaded';
      } else if (percentage >= 75) {
        eventData.burden = 'heavy';
      } else {
        eventData.burden = 'light';
      }
    }
  }
  
  // Check action verb for variations
  const verb = context.command.parsed.structure.verb?.text.toLowerCase() || 'inventory';
  if (verb === 'i' || verb === 'inv') {
    // Short form, brief output
    eventData.brief = true;
  }
  
  const params: Record<string, any> = {};
  
  // Determine appropriate message based on inventory state
  let messageId = 'carrying';
  
  if (carried.length === 0) {
    messageId = 'inventory_empty';
    // Vary the empty message
    const emptyMessages = ['inventory_empty', 'nothing_at_all', 'hands_empty', 'pockets_empty'];
    messageId = emptyMessages[Math.floor(Math.random() * emptyMessages.length)];
  } else if (holding.length > 0 && worn.length > 0) {
    messageId = 'carrying_and_wearing';
    params.holdingCount = holding.length;
    params.wearingCount = worn.length;
  } else if (worn.length > 0 && holding.length === 0) {
    messageId = 'wearing';
    params.wearingCount = worn.length;
  } else {
    params.holdingCount = holding.length;
  }
  
  // Prepare lists for display
  const holdingList = holding.length > 0 ? holding.map(e => e.name).join(', ') : undefined;
  const wornList = worn.length > 0 ? worn.map(e => e.name).join(', ') : undefined;
  const burdenMessage = eventData.burden && carried.length > 0 ? `burden_${eventData.burden}` : undefined;
  
  return {
    holding,
    worn,
    carried,
    eventData,
    messageId,
    params,
    holdingList,
    wornList,
    burdenMessage,
    totalWeight,
    weightLimit
  };
}

export const inventoryAction: Action & { metadata: ActionMetadata } = {
  id: IFActions.INVENTORY,
  requiredMessages: [
    'inventory_empty',
    'carrying',
    'wearing',
    'carrying_and_wearing',
    'holding_list',
    'worn_list',
    'checking_pockets',
    'rifling_through_bag',
    'inventory_header',
    'nothing_at_all',
    'hands_empty',
    'pockets_empty',
    'carrying_count',
    'wearing_count',
    'burden_light',
    'burden_heavy',
    'burden_overloaded'
  ],
  
  validate(context: ActionContext): ValidationResult {
    // Inventory check is always valid - no preconditions
    return { valid: true };
  },

  execute(context: ActionContext): void {
    // Inventory has NO world mutations
    // Analyze inventory and store in sharedData for report phase
    const analysis = analyzeInventory(context);
    const sharedData = getInventorySharedData(context);

    sharedData.analysis = analysis;
  },

  report(context: ActionContext): ISemanticEvent[] {
    // report() is only called on success - inventory always succeeds
    const events: ISemanticEvent[] = [];
    const sharedData = getInventorySharedData(context);
    const analysis = sharedData.analysis;

    if (!analysis) {
      return events;
    }

    // Emit the main inventory event with messageId for text rendering
    // Include all data for comprehensive event handling
    events.push(context.event('if.event.inventory', {
      messageId: `${context.action.id}.${analysis.messageId}`,
      params: {
        ...analysis.params,
        holdingList: analysis.holdingList,
        wornList: analysis.wornList
      },
      ...analysis.eventData
    }));

    // Add item lists if not empty
    if (analysis.holdingList) {
      events.push(context.event('if.event.inventory', {
        messageId: `${context.action.id}.${InventoryMessages.HOLDING_LIST}`,
        params: { items: analysis.holdingList },
        isHoldingList: true
      }));
    }

    if (analysis.wornList) {
      events.push(context.event('if.event.inventory', {
        messageId: `${context.action.id}.${InventoryMessages.WORN_LIST}`,
        params: { items: analysis.wornList },
        isWornList: true
      }));
    }

    // Add burden status if relevant
    if (analysis.burdenMessage) {
      events.push(context.event('if.event.inventory', {
        messageId: `${context.action.id}.${analysis.burdenMessage}`,
        params: { weight: analysis.totalWeight, limit: analysis.weightLimit },
        isBurdenMessage: true
      }));
    }

    return events;
  },

  blocked(context: ActionContext, result: ValidationResult): ISemanticEvent[] {
    // blocked() is called when validation fails
    // Inventory always succeeds, so this should never be called
    return [context.event('if.event.inventory', {
      blocked: true,
      messageId: `${context.action.id}.${result.error}`,
      params: result.params || {},
      reason: result.error
    })];
  },

  group: "meta",

  metadata: {
    requiresDirectObject: false,
    requiresIndirectObject: false
  }
};